import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useToast } from "@jobenvoy/ui";
import { useSession } from "@/auth/session";
import { placeAds, trackSignup } from "@/lib/ads";
import { stopAnalytics } from "@/lib/analytics";
import { trackConversion } from "@/lib/conversions";
import {
    accountJustMade,
    acceptanceNeeded,
    ageRecord,
    analyticsInForce,
    analyticsRecord,
    analyticsUnasked,
    consentError,
    privacySignal,
    readSignupAcceptance,
    recordAnalyticsOn,
    forgetSignupAcceptance,
    signupMatches,
    storageOf,
    termsInForce,
    termsRecord,
} from "@/lib/consent";
import { useAsksFirst } from "@/lib/hooks/analyticsRegion";
import { useConsents, useRecordConsent } from "@/lib/hooks/consents";
import AnalyticsBanner from "./AnalyticsBanner";
import { analyticsAskable, AnalyticsChoiceContext, turnAnalyticsOn } from "./analyticsChoice";
import TermsPrompt from "./TermsPrompt";

/**
 * What a signed-in person agreed to, over every page of theirs. Read
 * from the auth service:
 *
 * - The sign-up sheet's ticked box, kept across the sign-in, is recorded
 *   as the sign-up's (`SIGNUP_CHECKBOX`), when it was for the versions in
 *   force and the record still needs it. Once recorded for an account
 *   that never agreed before, the ad partners count the
 *   sign-up (lib/ads), and so does PostHog (lib/conversions).
 * - Otherwise a person whose terms are MISSING or BEHIND, or whose age is
 *   MISSING, meets a prompt that cannot be closed (`REACCEPT_PROMPT`).
 *   An account made just now (a first Google sign-in, which skips the
 *   sheet) that agrees there is counted as a sign-up the same way; an
 *   older account that never agreed is not.
 * - Analytics follows the record (`recordAnalyticsOn`). In the European
 *   Economic Area, the United Kingdom and Switzerland, and wherever the
 *   place cannot be read, it runs only while the record says GRANTED, and
 *   a person never asked, or asked under older words, is asked in the
 *   banner (`CONSENT_BANNER`) unless their browser sends Global Privacy
 *   Control. Elsewhere it is on unless they said no or send Global Privacy
 *   Control, and nobody is asked, but session replay waits for a grant.
 *   The account page has the switch, and the footer's link opens the
 *   banner anywhere.
 * - The ad partners follow analytics, and only where nothing asks first and
 *   the browser sends no Global Privacy Control (lib/ads).
 *
 * A read that fails blocks nothing and starts nothing: the prompt waits
 * for an answer, and analytics stays off.
 */
export default function ConsentGate({ children }: { children: ReactNode }) {
    const session = useSession();
    const toast = useToast();
    const consents = useConsents();
    const record = useRecordConsent();
    const choose = useRecordConsent();
    const askFirst = useAsksFirst();
    const data = consents.data;

    // The sign-up's ticked box, read once as the page opens, and judged
    // once against the first read: sent when it is for the versions in
    // force and the record still needs it, dropped otherwise.
    const [signup] = useState(() => readSignupAcceptance(storageOf("sessionStorage")));
    const [signupPlan, setSignupPlan] = useState<"unread" | "send" | "skip">("unread");
    const [newAccount, setNewAccount] = useState(false);
    const needed = data ? acceptanceNeeded(data) : "none";
    if (signupPlan === "unread" && data) {
        setSignupPlan(
            signup && needed !== "none" && signupMatches(signup, data.required) ? "send" : "skip",
        );
        setNewAccount(data.terms.status === "MISSING");
    }
    const signupSent = useRef(false);
    const { userId } = session;
    useEffect(() => {
        if (signupPlan === "unread" || signupSent.current) return;
        signupSent.current = true;
        if (signup) forgetSignupAcceptance(storageOf("sessionStorage"));
        if (signupPlan === "send")
            record.mutate(termsRecord("SIGNUP_CHECKBOX"), {
                onSuccess: () => {
                    if (!newAccount) return;
                    trackSignup(userId);
                    trackConversion({ name: "signed_up" });
                },
            });
    }, [signupPlan, signup, record, newAccount, userId]);
    // While the sign-up's own record is on its way, the prompt waits.
    const signupInFlight =
        signupPlan === "unread" || (signupPlan === "send" && !record.isError && !record.isSuccess);

    // Analytics follows the record, and is off while the record is unread.
    // Until the place is read, only a grant turns it on.
    // Session replay runs only on a grant, wherever the person is.
    const analyticsOn = data ? recordAnalyticsOn(data, askFirst ?? true, privacySignal()) : false;
    const recording = data?.analytics.status === "GRANTED";
    useEffect(() => {
        if (analyticsOn) turnAnalyticsOn("record", session.userId, recording);
        else stopAnalytics("record");
    }, [analyticsOn, recording, session.userId]);
    useEffect(() => placeAds(askFirst === false && !privacySignal()), [askFirst]);

    const [reopened, setReopened] = useState(false);
    const [declined, setDeclined] = useState(false);
    const choice = useMemo(() => ({ open: () => setReopened(true) }), []);

    const prompting = data !== undefined && needed !== "none" && !signupInFlight;
    const asking =
        data !== undefined &&
        !prompting &&
        needed === "none" &&
        analyticsAskable() &&
        analyticsInForce(data.required) &&
        (reopened ||
            (askFirst === true && analyticsUnasked(data) && !declined && !privacySignal()));

    const allow = () =>
        choose.mutate(analyticsRecord(true, "CONSENT_BANNER"), {
            onSuccess: () => {
                turnAnalyticsOn("choice", session.userId);
                setReopened(false);
            },
            onError: (error) => toast.error("Analytics not turned on", consentError(error)),
        });
    const decline = () => {
        // A no stops everything now, whatever the record's answer.
        stopAnalytics("choice");
        setDeclined(true);
        setReopened(false);
        choose.mutate(analyticsRecord(false, "CONSENT_BANNER"), {
            onError: (error) =>
                toast.error(
                    "Analytics is off on this page, but your choice was not saved",
                    consentError(error),
                ),
        });
    };

    return (
        <AnalyticsChoiceContext.Provider value={choice}>
            {children}
            {prompting && data && (
                <TermsPrompt
                    ask={needed === "age" ? "age" : "terms"}
                    status={data.terms.status}
                    stale={!termsInForce(data.required)}
                    busy={record.isPending}
                    error={record.isError ? consentError(record.error) : null}
                    onAccept={() =>
                        record.mutate(
                            needed === "age" ? ageRecord() : termsRecord("REACCEPT_PROMPT"),
                            {
                                onSuccess: () => {
                                    if (
                                        needed !== "age" &&
                                        newAccount &&
                                        accountJustMade(session.createdAt)
                                    )
                                        trackSignup(userId);
                                },
                            },
                        )
                    }
                    onSignOut={session.signOut}
                />
            )}
            {asking && (
                <AnalyticsBanner
                    byDefault={askFirst === false}
                    busy={choose.isPending}
                    onAllow={allow}
                    onDecline={decline}
                />
            )}
        </AnalyticsChoiceContext.Provider>
    );
}
